import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { ResignReason } from "../../../../types/onboarding";

interface ResignReasonDialogProps {
  visible: boolean;
  reason: ResignReason | null; // null = add mode
  onHide: () => void;
  onSubmit: (code: string, displayLabel: string) => Promise<boolean>;
}

// Codes are uppercase snake_case.
const normaliseCode = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const ResignReasonDialog: React.FC<ResignReasonDialogProps> = ({
  visible,
  reason,
  onHide,
  onSubmit,
}) => {
  const isEdit = !!reason;
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setCode(reason?.code ?? "");
    setLabel(reason?.display_label ?? "");
    setError(null);
  }, [visible, reason]);

  const submit = async () => {
    const finalCode = isEdit ? reason!.code : normaliseCode(code);
    if (!isEdit && !finalCode) {
      setError("A code is required.");
      return;
    }
    if (!label.trim()) {
      setError("A display label is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ok = await onSubmit(finalCode, label.trim());
      if (ok) {
        onHide();
      } else if (!isEdit) {
        setError("That code is already in use, or it could not be saved.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button label="Save" icon="pi pi-check" onClick={submit} loading={submitting} />
    </div>
  );

  return (
    <Dialog
      header={isEdit ? "Edit reason" : "Add reason"}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "30rem" }}
    >
      {!isEdit && (
        <div className="field">
          <label htmlFor="reason-code" className="input-label">
            Code *
          </label>
          <InputText
            id="reason-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. COMPETITOR_CHOSEN"
            className="w-full"
          />
        </div>
      )}
      <div className="field">
        <label htmlFor="reason-label" className="input-label">
          Display label *
        </label>
        <InputText
          id="reason-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="w-full"
        />
      </div>
      {error && <small className="p-error block">{error}</small>}
    </Dialog>
  );
};

export default ResignReasonDialog;
